import intoleranceRules from '@/data/intolerance_ruleset.json';

// ---------- Utils (module scope) ----------

const normalizeText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/['’]/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const tokenize = (value) => {
  const normalized = normalizeText(value);
  return normalized ? normalized.split(" ") : [];
};

const hasTokenSequence = (textTokens, keywordTokens) => {
  if (!keywordTokens.length || !textTokens.length) return false;

  const lastStart = textTokens.length - keywordTokens.length;
  for (let i = 0; i <= lastStart; i += 1) {
    let matches = true;
    for (let j = 0; j < keywordTokens.length; j += 1) {
      if (textTokens[i + j] !== keywordTokens[j]) {
        matches = false;
        break;
      }
    }
    if (matches) return true;
  }
  return false;
};

const hasContextualMatch = (textTokens, keywordTokens, context) => {
  const { requireOneOf = [], window = 3 } = context || {};
  if (!keywordTokens.length || !textTokens.length) return false;

  // Tokens contextuels (flatten)
  const contextTokens = requireOneOf.map(tokenize).flat().filter(Boolean);
  if (!contextTokens.length) return false;

  const lastStart = textTokens.length - keywordTokens.length;

  for (let i = 0; i <= lastStart; i += 1) {
    // 1) match du keyword à l'index i ?
    let matches = true;
    for (let j = 0; j < keywordTokens.length; j += 1) {
      if (textTokens[i + j] !== keywordTokens[j]) {
        matches = false;
        break;
      }
    }
    if (!matches) continue;

    // 2) fenêtre de contexte autour
    const start = Math.max(0, i - window);
    const end = Math.min(textTokens.length, i + keywordTokens.length + window);
    const surrounding = textTokens.slice(start, end);

    // 3) présence d’au moins un token contextuel
    if (contextTokens.some((ct) => surrounding.includes(ct))) {
      return true;
    }
  }

  return false;
};

// ---------- Service ----------

/**
 * Vérifie si un produit Open Food Facts respecte les contraintes d’une intolérance donnée.
 *
 * @param {Object} product - Données du produit (issu de Open Food Facts)
 * @param {Object} constraint - Règles (keywords, tags, fields, mustNotContain, context)
 * @returns {boolean} true si le produit respecte les contraintes (OK), false sinon
 */
export function checkProductForIntolerance(product, constraint) {
  const {
    keywords = [],
    tags = [],
    fields = [],
    mustNotContain = true,
    context = null,
  } = constraint || {};

  // 1) texte source (fields + ingrédients)
  const textFields = (fields || [])
    .map((field) => product?.[field])
    .filter(Boolean)
    .map((t) => String(t))
    .join(" ");

  let ingredientsText = "";
  if (Array.isArray(product?.ingredients)) {
    ingredientsText = product.ingredients
      .map((i) => `${i?.text ?? ""} ${i?.id ?? ""}`)
      .join(" ");
  }

  const allText = `${textFields} ${ingredientsText}`;
  const allTokens = tokenize(allText);

  // 2) match keywords (avec ou sans contexte)
  const keywordMatch = (keywords || []).some((keyword) => {
    const keywordTokens = tokenize(keyword);
    if (!keywordTokens.length) return false;

    if (context) {
      return hasContextualMatch(allTokens, keywordTokens, context);
    }

    return hasTokenSequence(allTokens, keywordTokens);
  });

  // 3) match tags (inchangé)
  const allTags = [
    ...(product?.allergens_tags || []),
    ...(product?.ingredients_tags || []),
  ].map((t) => String(t).toLowerCase());

  const tagMatch = (tags || []).some((kw) => {
    const kwTokens = tokenize(kw);
    if (!kwTokens.length) return false;

    return allTags.some((tag) => hasTokenSequence(tokenize(tag), kwTokens));
  });

  const hasMatch = keywordMatch || tagMatch;

  // 4) logique mustNotContain
  return mustNotContain ? !hasMatch : hasMatch;
}

export function checkAllIntolerances(product, userIntolerances, rules) {
  const results = {};
  for (const intolerance of userIntolerances) {
    const rule = rules?.[intolerance];
    if (!rule) continue;
    results[intolerance] = checkProductForIntolerance(product, rule);
  }
  return results;
}

export function checkProductWithIntolerances(product, intolerances) {
  const notSafeIntolerances = [];
  const rules = intoleranceRules;

  for (const intolerance of intolerances) {
    const rule = rules?.[intolerance];
    if (!rule) continue;

    const isSafe = checkProductForIntolerance(product, rule);
    if (!isSafe) notSafeIntolerances.push(intolerance);
  }

  return notSafeIntolerances;
}

export const formatAllergen = (key) => {
  return String(key ?? "")
    .replace(/^en:/, "")
    .replace("-", " ")
    .replace(/^\w/, (c) => c.toUpperCase());
};
